import {
  Anchor,
  Button,
  Container,
  FileInput,
  Group,
  Image,
  MultiSelect,
  Select,
  Stack,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  createKitabaBook,
  getKitabaBook,
  updateKitabaBook,
} from '@/entities/books/api/admin-books-api';
import { Api } from '@/shared/api';
import { uploadKitabaFile } from '@/shared/api/upload';
import { useDirtyForm } from '@/shared/hooks/use-dirty-form';
import { useProjectPath } from '@/shared/projects/project-path';

type FormValues = {
  title: string;
  authorId: string | null;
  publisherId: string | null;
  categoriesIds: string[];
  translatorName: string;
  description: string;
  coverFile: File | null;
  epubFile: File | null;
  isPublished: boolean;
};

export function KitabaBookDetailPage() {
  const { bookId } = useParams();
  const isNew = !bookId || bookId === 'new';
  const navigate = useNavigate();
  const withProject = useProjectPath();
  const [coverUrl, setCoverUrl] = useState('');
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [authors, setAuthors] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [categories, setCategories] = useState<
    { value: string; label: string }[]
  >([]);
  const [publishers, setPublishers] = useState<
    { value: string; label: string }[]
  >([]);
  const [hasPublisher, setHasPublisher] = useState(false);

  const form = useForm<FormValues>({
    initialValues: {
      title: '',
      authorId: null,
      publisherId: null,
      categoriesIds: [],
      translatorName: '',
      description: '',
      coverFile: null,
      epubFile: null,
      isPublished: false,
    },
    validate: {
      title: (value) => (value.trim() ? null : 'Название обязательно'),
      authorId: (value) => (value ? null : 'Автор обязателен'),
      coverFile: (value) => (isNew && !value ? 'Обложка обязательна' : null),
    },
  });

  useDirtyForm(form.isDirty());

  useEffect(() => {
    const loadReferences = async () => {
      try {
        const [authorsResponse, categoriesResponse, publishersResponse] =
          await Promise.all([
            Api.authors.getAuthors('kitaba'),
            Api.categories.getCategories('kitaba'),
            Api.publishers.getPublishers('kitaba'),
          ]);

        setAuthors(
          authorsResponse.authors.map((author) => ({
            value: String(author.id),
            label: author.name,
          })),
        );
        setCategories(
          categoriesResponse.categories.map((category) => ({
            value: String(category.id),
            label: category.title,
          })),
        );
        setPublishers(
          publishersResponse.publishers.map((publisher) => ({
            value: String(publisher.id),
            label: publisher.title,
          })),
        );
      } catch {
        notifications.show({
          message: 'Не удалось загрузить справочники книги',
          color: 'red',
        });
      }
    };

    void loadReferences();
  }, []);

  useEffect(() => {
    if (isNew || !bookId) return;

    const loadBook = async () => {
      try {
        const book = await getKitabaBook(bookId);
        form.setValues({
          title: book.title,
          authorId: book.authorId ? String(book.authorId) : null,
          publisherId: book.publisherId ? String(book.publisherId) : null,
          categoriesIds: book.categoryIds.map(String),
          translatorName: book.translatorName,
          description: book.description,
          coverFile: null,
          epubFile: null,
          isPublished: book.isPublished,
        });
        form.resetDirty();
        setCoverUrl(book.imgUrl);
        setFileUrl(book.fileUrl || null);
        setHasPublisher(Boolean(book.publisherId));
      } catch {
        notifications.show({
          message: 'Не удалось загрузить книгу',
          color: 'red',
        });
      }
    };

    void loadBook();
  }, [bookId, isNew]);

  const handleSubmit = async (values: FormValues) => {
    if (values.isPublished && !coverUrl && !values.coverFile) {
      form.setFieldError('coverFile', 'Обложка обязательна для публикации');
      return;
    }

    if (values.isPublished && !fileUrl && !values.epubFile) {
      form.setFieldError('epubFile', 'EPUB обязателен для публикации');
      return;
    }

    try {
      const [cover, epub] = await Promise.all([
        values.coverFile
          ? uploadKitabaFile(values.coverFile, 'books', 'image')
          : null,
        values.epubFile
          ? uploadKitabaFile(values.epubFile, 'books', 'epub')
          : null,
      ]);

      const payload = {
        authorId: Number(values.authorId),
        title: values.title.trim(),
        description: values.description,
        translatorName: values.translatorName,
        categoriesIds: values.categoriesIds.map(Number),
        ...(values.publisherId && { publisherId: Number(values.publisherId) }),
        ...(cover && { coverUrl: cover.path }),
        ...(epub && { file: epub.path }),
      };

      if (isNew) {
        await createKitabaBook({ ...payload, weight: 0 });
      } else if (bookId) {
        await updateKitabaBook(bookId, {
          ...payload,
          isPublished: values.isPublished,
        });
      }

      form.resetDirty();
      navigate(withProject('/books'));
    } catch (error) {
      notifications.show({
        message: 'Не удалось сохранить книгу',
        color: 'red',
      });
    }
  };

  return (
    <Container size={1000} mt={50}>
      <Title order={1} mb={24}>
        {isNew ? 'Добавление книги Kitaba' : 'Редактирование книги Kitaba'}
      </Title>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            label="Название"
            required
            {...form.getInputProps('title')}
          />
          <Select
            label="Автор"
            required
            data={authors}
            {...form.getInputProps('authorId')}
          />
          <MultiSelect
            label="Категории"
            data={categories}
            clearable
            {...form.getInputProps('categoriesIds')}
          />
          <Select
            label="Издательство"
            data={publishers}
            clearable={!hasPublisher}
            allowDeselect={!hasPublisher}
            {...form.getInputProps('publisherId')}
          />
          <TextInput
            label="Переводчик"
            {...form.getInputProps('translatorName')}
          />
          <Textarea
            label="Описание"
            autosize
            minRows={3}
            {...form.getInputProps('description')}
          />
          {!isNew && (
            <Select
              label="Статус"
              value={form.values.isPublished ? 'published' : 'draft'}
              onChange={(value) =>
                form.setFieldValue('isPublished', value === 'published')
              }
              data={[
                { value: 'draft', label: 'Черновик' },
                { value: 'published', label: 'Опубликовано' },
              ]}
            />
          )}
          <FileInput
            label="Обложка"
            required={isNew}
            accept="image/png,image/jpeg"
            {...form.getInputProps('coverFile')}
          />
          {coverUrl && <Image src={coverUrl} alt="Обложка" maw={180} />}
          <FileInput
            label="EPUB"
            accept="application/epub+zip,.epub"
            description="Необязателен для черновика, обязателен для публикации"
            {...form.getInputProps('epubFile')}
          />
          {fileUrl && (
            <Anchor href={fileUrl} target="_blank" rel="noreferrer">
              Открыть текущий EPUB
            </Anchor>
          )}
          <Group mt="md">
            <Button type="submit">Сохранить</Button>
            <Button
              variant="outline"
              color="gray"
              onClick={() => navigate(withProject('/books'))}
            >
              Отменить
            </Button>
          </Group>
        </Stack>
      </form>
    </Container>
  );
}
